import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line,
} from 'recharts';
import { generateToken, TOKEN_HEADER } from '../utils/tokenUtils';
import { API_ENDPOINTS } from '../config/constants';
import SEO from '../components/SEO';
import '../styles/survey-dashboard.css';

// ─── Constants ────────────────────────────────────────────────────────────────

const PALETTE = ['#de2c1e', '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

const CHOICE_TYPES   = new Set(['multiple_choice', 'checkbox', 'dropdown']);
const RATING_TYPES   = new Set(['rating']);
const TEXT_TYPES     = new Set(['text', 'textarea']);
const YES_NO_TYPES   = new Set(['yes_no']);

// ─── OTP Gate ────────────────────────────────────────────────────────────────

function OTPGate({ onAuth }) {
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');

  const getTodayOTP = () => {
    const d = new Date();
    return `${String(d.getDate()).padStart(2,'0')}${String(d.getMonth()+1).padStart(2,'0')}${d.getFullYear()}`;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (otp === getTodayOTP()) {
      sessionStorage.setItem('adminAuth', 'true');
      sessionStorage.setItem('adminLoginTime', Date.now().toString());
      onAuth();
    } else {
      setError('Invalid OTP. Please try again.');
      setOtp('');
    }
  };

  return (
    <main className="sd-page sd-auth-page">
      <SEO title="Survey Dashboard · LWT" />
      <div className="sd-auth-card">
        <div className="sd-auth-icon">📊</div>
        <h1>Survey Dashboard</h1>
        <p className="sd-auth-hint">Enter today's access code to continue</p>
        <form onSubmit={handleSubmit} className="sd-auth-form">
          <input
            type="password"
            value={otp}
            onChange={e => { setOtp(e.target.value); setError(''); }}
            placeholder="Enter OTP"
            className="sd-auth-input"
            autoFocus
          />
          {error && <p className="sd-auth-error">{error}</p>}
          <button type="submit" className="sd-btn-primary">Access Dashboard</button>
        </form>
      </div>
    </main>
  );
}

// ─── KPI Card ────────────────────────────────────────────────────────────────

function KpiCard({ icon, label, value, sub, color }) {
  return (
    <div className="sd-kpi-card" style={{ '--kpi-color': color }}>
      <div className="sd-kpi-icon">{icon}</div>
      <div className="sd-kpi-body">
        <p className="sd-kpi-label">{label}</p>
        <p className="sd-kpi-value">{value ?? '—'}</p>
        {sub && <p className="sd-kpi-sub">{sub}</p>}
      </div>
    </div>
  );
}

// ─── Chart toggle tabs ────────────────────────────────────────────────────────

function ChartTabs({ tabs, active, onChange }) {
  return (
    <div className="sd-chart-tabs">
      {tabs.map(t => (
        <button
          key={t.id}
          className={`sd-chart-tab ${active === t.id ? 'active' : ''}`}
          onClick={() => onChange(t.id)}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ─── Choice question chart (Bar + Pie) ───────────────────────────────────────

function ChoiceChart({ data, questionType }) {
  const [view, setView] = useState('bar');
  const total = data.reduce((s, d) => s + d.count, 0);

  const barData = data.map(d => ({ name: d.label, Responses: d.count }));
  const pieData = data.map(d => ({ name: d.label, value: d.count }));

  const renderCustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const { name, value } = payload[0];
    const pct = total ? ((value / total) * 100).toFixed(1) : 0;
    return (
      <div className="sd-tooltip">
        <p className="sd-tooltip-label">{name}</p>
        <p className="sd-tooltip-value">{value} responses ({pct}%)</p>
      </div>
    );
  };

  const RADIAN = Math.PI / 180;
  const renderPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    if (percent < 0.04) return null;
    const r = innerRadius + (outerRadius - innerRadius) * 0.55;
    const x = cx + r * Math.cos(-midAngle * RADIAN);
    const y = cy + r * Math.sin(-midAngle * RADIAN);
    return (
      <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central" fontSize={12} fontWeight={700}>
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <div className="sd-chart-wrap">
      <ChartTabs
        tabs={[{ id: 'bar', label: '📊 Bar' }, { id: 'pie', label: '🥧 Pie' }]}
        active={view}
        onChange={setView}
      />
      <div className="sd-chart-canvas">
        {view === 'bar' ? (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={barData} margin={{ top: 8, right: 16, left: 0, bottom: 50 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.07)" />
              <XAxis
                dataKey="name"
                tick={{ fill: '#9db0bf', fontSize: 12 }}
                angle={-30}
                textAnchor="end"
                interval={0}
              />
              <YAxis tick={{ fill: '#9db0bf', fontSize: 12 }} allowDecimals={false} />
              <Tooltip content={renderCustomTooltip} />
              <Bar dataKey="Responses" radius={[5, 5, 0, 0]}>
                {barData.map((_, i) => (
                  <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={pieData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={110}
                labelLine={false}
                label={renderPieLabel}
              >
                {pieData.map((_, i) => (
                  <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value, name) => [`${value} (${total ? ((value/total)*100).toFixed(1) : 0}%)`, name]}
                contentStyle={{ background: '#1a2a38', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8 }}
                labelStyle={{ color: '#e8eef3' }}
                itemStyle={{ color: '#e8eef3' }}
              />
              <Legend
                wrapperStyle={{ fontSize: 12, color: '#9db0bf' }}
                iconType="circle"
              />
            </PieChart>
          </ResponsiveContainer>
        )}
      </div>
      <p className="sd-total-label">Total responses: <strong>{total}</strong></p>
    </div>
  );
}

// ─── Rating chart (Bar + Line) ────────────────────────────────────────────────

function RatingChart({ data, ratingMax }) {
  const [view, setView] = useState('bar');
  const total = data.reduce((s, d) => s + d.count, 0);
  const avg   = total ? (data.reduce((s, d) => s + d.rating * d.count, 0) / total).toFixed(2) : '—';

  const chartData = Array.from({ length: ratingMax || 10 }, (_, i) => {
    const n = i + 1;
    const match = data.find(d => d.rating === n);
    return { rating: String(n), Responses: match ? match.count : 0 };
  });

  const ratingTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const { name, value } = payload[0];
    const pct = total ? ((value / total) * 100).toFixed(1) : 0;
    return (
      <div className="sd-tooltip">
        <p className="sd-tooltip-label">Rating {payload[0].payload.rating}</p>
        <p className="sd-tooltip-value">{value} responses ({pct}%)</p>
      </div>
    );
  };

  return (
    <div className="sd-chart-wrap">
      <div className="sd-rating-avg">
        Average rating: <span className="sd-avg-value">{avg}</span>
        <span className="sd-avg-sub"> / {ratingMax || 10} · {total} responses</span>
      </div>
      <ChartTabs
        tabs={[{ id: 'bar', label: '📊 Bar' }, { id: 'line', label: '📈 Line' }]}
        active={view}
        onChange={setView}
      />
      <div className="sd-chart-canvas">
        <ResponsiveContainer width="100%" height={240}>
          {view === 'bar' ? (
            <BarChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.07)" />
              <XAxis dataKey="rating" tick={{ fill: '#9db0bf', fontSize: 12 }} />
              <YAxis tick={{ fill: '#9db0bf', fontSize: 12 }} allowDecimals={false} />
              <Tooltip content={ratingTooltip} />
              <Bar dataKey="Responses" fill="#de2c1e" radius={[5, 5, 0, 0]}>
                {chartData.map((_, i) => (
                  <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                ))}
              </Bar>
            </BarChart>
          ) : (
            <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.07)" />
              <XAxis dataKey="rating" tick={{ fill: '#9db0bf', fontSize: 12 }} />
              <YAxis tick={{ fill: '#9db0bf', fontSize: 12 }} allowDecimals={false} />
              <Tooltip content={ratingTooltip} />
              <Line
                type="monotone"
                dataKey="Responses"
                stroke="#de2c1e"
                strokeWidth={2.5}
                dot={{ fill: '#de2c1e', r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ─── Yes/No chart ─────────────────────────────────────────────────────────────

function YesNoChart({ data }) {
  const total = data.reduce((s, d) => s + d.count, 0);
  const pieData = data.map(d => ({ name: d.label, value: d.count }));
  const YES_COLORS = ['#10b981', '#ef4444'];

  return (
    <div className="sd-chart-wrap sd-yesno-wrap">
      <ResponsiveContainer width="100%" height={220}>
        <PieChart>
          <Pie
            data={pieData}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            outerRadius={90}
            innerRadius={45}
          >
            {pieData.map((_, i) => (
              <Cell key={i} fill={YES_COLORS[i % YES_COLORS.length]} />
            ))}
          </Pie>
          <Tooltip
            formatter={(value, name) => [`${value} (${total ? ((value/total)*100).toFixed(1) : 0}%)`, name]}
            contentStyle={{ background: '#1a2a38', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8 }}
            labelStyle={{ color: '#e8eef3' }}
            itemStyle={{ color: '#e8eef3' }}
          />
          <Legend wrapperStyle={{ fontSize: 13, color: '#9db0bf' }} iconType="circle" />
        </PieChart>
      </ResponsiveContainer>
      <p className="sd-total-label">Total responses: <strong>{total}</strong></p>
    </div>
  );
}

// ─── Text responses list ──────────────────────────────────────────────────────

function TextResponses({ answers }) {
  const [showAll, setShowAll] = useState(false);
  const items = showAll ? answers : answers.slice(0, 5);

  if (!answers.length) {
    return <p className="sd-no-data">No text responses yet.</p>;
  }

  return (
    <div className="sd-text-responses">
      {items.map((a, i) => (
        <div key={i} className="sd-text-bubble">
          <span className="sd-text-idx">{i + 1}</span>
          <p>{a}</p>
        </div>
      ))}
      {answers.length > 5 && (
        <button className="sd-show-more-btn" onClick={() => setShowAll(v => !v)}>
          {showAll ? 'Show less' : `Show all ${answers.length} responses`}
        </button>
      )}
    </div>
  );
}

// ─── Per-question analytics block ────────────────────────────────────────────

function QuestionBlock({ question, analytics, responses }) {
  const { id, question_text, question_type, options = [], rating_max } = question;

  // Build chart data from analytics (option counts) or from raw text answers
  const chartData = useMemo(() => {
    if (CHOICE_TYPES.has(question_type) || YES_NO_TYPES.has(question_type)) {
      const optionRows = analytics.filter(a => a.question_id === id);
      if (question_type === 'yes_no') {
        const countMap = {};
        responses.forEach(r => {
          r.answers
            .filter(a => a.question_id === id)
            .forEach(a => {
              const k = a.answer_text || 'Unknown';
              countMap[k] = (countMap[k] || 0) + 1;
            });
        });
        return Object.entries(countMap).map(([label, count]) => ({ label, count }));
      }
      return optionRows.map(r => ({ label: r.option_text, count: r.selection_count || 0 }));
    }

    if (RATING_TYPES.has(question_type)) {
      const countMap = {};
      responses.forEach(r => {
        r.answers
          .filter(a => a.question_id === id)
          .forEach(a => {
            const n = parseInt(a.answer_text, 10);
            if (!isNaN(n)) countMap[n] = (countMap[n] || 0) + 1;
          });
      });
      return Object.entries(countMap).map(([k, v]) => ({ rating: parseInt(k, 10), count: v }));
    }

    return [];
  }, [id, question_type, analytics, responses]);

  const textAnswers = useMemo(() => {
    if (!TEXT_TYPES.has(question_type)) return [];
    return responses
      .flatMap(r => r.answers.filter(a => a.question_id === id).map(a => a.answer_text))
      .filter(Boolean);
  }, [id, question_type, responses]);

  const typeLabel = {
    multiple_choice: 'Multiple Choice',
    checkbox:        'Checkbox',
    dropdown:        'Dropdown',
    rating:          'Rating',
    text:            'Short Text',
    textarea:        'Long Text',
    yes_no:          'Yes / No',
    date:            'Date',
  }[question_type] || question_type;

  return (
    <div className="sd-q-block">
      <div className="sd-q-header">
        <span className="sd-q-type-badge">{typeLabel}</span>
        <p className="sd-q-text">{question_text}</p>
      </div>

      <div className="sd-q-chart">
        {CHOICE_TYPES.has(question_type) && chartData.length > 0 && (
          <ChoiceChart data={chartData} questionType={question_type} />
        )}
        {YES_NO_TYPES.has(question_type) && chartData.length > 0 && (
          <YesNoChart data={chartData} />
        )}
        {RATING_TYPES.has(question_type) && (
          <RatingChart data={chartData} ratingMax={rating_max || 5} />
        )}
        {TEXT_TYPES.has(question_type) && (
          <TextResponses answers={textAnswers} />
        )}
        {question_type === 'date' && (
          <TextResponses answers={responses.flatMap(r => r.answers.filter(a => a.question_id === id).map(a => a.answer_text)).filter(Boolean)} />
        )}
        {(CHOICE_TYPES.has(question_type) || YES_NO_TYPES.has(question_type)) && chartData.length === 0 && (
          <p className="sd-no-data">No responses yet.</p>
        )}
      </div>
    </div>
  );
}

// ─── Submissions timeline chart ───────────────────────────────────────────────

function SubmissionsTimeline({ responses }) {
  const data = useMemo(() => {
    const byDay = {};
    responses.forEach(r => {
      const day = new Date(r.submitted_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
      byDay[day] = (byDay[day] || 0) + 1;
    });
    return Object.entries(byDay)
      .sort((a, b) => new Date(a[0]) - new Date(b[0]))
      .map(([date, count]) => ({ date, Submissions: count }));
  }, [responses]);

  if (data.length < 2) return null;

  return (
    <div className="sd-timeline-wrap">
      <h3 className="sd-section-subtitle">📅 Submissions Over Time</h3>
      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.07)" />
          <XAxis dataKey="date" tick={{ fill: '#9db0bf', fontSize: 11 }} />
          <YAxis tick={{ fill: '#9db0bf', fontSize: 12 }} allowDecimals={false} />
          <Tooltip
            contentStyle={{ background: '#1a2a38', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8 }}
            labelStyle={{ color: '#e8eef3' }}
            itemStyle={{ color: '#e8eef3' }}
          />
          <Line
            type="monotone"
            dataKey="Submissions"
            stroke="#de2c1e"
            strokeWidth={2.5}
            dot={{ fill: '#de2c1e', r: 3 }}
            activeDot={{ r: 6 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────

export default function SurveyDashboard() {
  // ── Auth ───────────────────────────────────────────────────────────────
  const [authed, setAuthed] = useState(() => {
    const auth = sessionStorage.getItem('adminAuth');
    const ts   = sessionStorage.getItem('adminLoginTime');
    if (auth === 'true' && ts) {
      return (Date.now() - parseInt(ts, 10)) < 8 * 60 * 60 * 1000;
    }
    return false;
  });

  // ── Data ───────────────────────────────────────────────────────────────
  const [surveys, setSurveys]               = useState([]);
  const [surveysLoading, setSurveysLoading] = useState(false);

  const [selectedId, setSelectedId]         = useState('');
  const [surveyDetail, setSurveyDetail]     = useState(null);  // { survey, responses, analytics, pagination }
  const [detailLoading, setDetailLoading]   = useState(false);
  const [detailPage, setDetailPage]         = useState(1);
  const [allResponses, setAllResponses]     = useState([]);    // accumulated across pages

  // ── Fetch all surveys (for the selector) ──────────────────────────────
  const fetchSurveys = useCallback(async () => {
    setSurveysLoading(true);
    try {
      const res  = await fetch(`${API_ENDPOINTS.BASE_URL}/surveys?limit=100`, {
        headers: { [TOKEN_HEADER]: generateToken() },
      });
      const data = await res.json();
      if (res.ok && data.success) setSurveys(data.data);
    } catch (e) {
      console.error('fetchSurveys', e);
    } finally {
      setSurveysLoading(false);
    }
  }, []);

  useEffect(() => { if (authed) fetchSurveys(); }, [authed, fetchSurveys]);

  // ── Fetch responses page by page, accumulate ──────────────────────────
  const fetchResponsePage = useCallback(async (id, page) => {
    setDetailLoading(true);
    try {
      const res  = await fetch(`${API_ENDPOINTS.BASE_URL}/surveys/${id}/responses?page=${page}&limit=200`, {
        headers: { [TOKEN_HEADER]: generateToken() },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSurveyDetail(data.data);
        if (page === 1) {
          setAllResponses(data.data.responses);
        } else {
          setAllResponses(prev => [...prev, ...data.data.responses]);
        }
        setDetailPage(page);
      }
    } catch (e) {
      console.error('fetchResponsePage', e);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const handleSurveySelect = (id) => {
    setSelectedId(id);
    setSurveyDetail(null);
    setAllResponses([]);
    setDetailPage(1);
    if (id) fetchResponsePage(id, 1);
  };

  const loadMoreResponses = () => {
    if (surveyDetail?.pagination && detailPage < surveyDetail.pagination.pages) {
      fetchResponsePage(selectedId, detailPage + 1);
    }
  };

  // ── Derived KPIs ───────────────────────────────────────────────────────
  const totalSurveys    = surveys.length;
  const activeSurveys   = surveys.filter(s => s.status === 'active').length;
  const totalResponses  = surveys.reduce((s, sv) => s + (sv.response_count || 0), 0);
  const totalQuestions  = surveys.reduce((s, sv) => s + (sv.question_count || 0), 0);

  const selectedSurveyMeta = surveys.find(s => s.id === Number(selectedId));

  // ── Questions from the detail (use survey inside surveyDetail) ─────────
  const detailQuestions = surveyDetail?.survey
    ? [] // analytics only has option counts; questions come from the full survey fetch below
    : [];

  // We need questions from a separate call — re-use the /take endpoint (public) or
  // the admin GET /surveys/:id. We'll cache it alongside the analytics.
  const [surveyMeta, setSurveyMeta] = useState(null);

  useEffect(() => {
    if (!selectedId) { setSurveyMeta(null); return; }
    fetch(`${API_ENDPOINTS.BASE_URL}/surveys/${selectedId}`, {
      headers: { [TOKEN_HEADER]: generateToken() },
    })
      .then(r => r.json())
      .then(d => { if (d.success) setSurveyMeta(d.data); })
      .catch(() => {});
  }, [selectedId]);

  if (!authed) {
    return <OTPGate onAuth={() => setAuthed(true)} />;
  }

  return (
    <main className="sd-page">
      <SEO title="Survey Dashboard · LWT" />

      {/* ── Header ──────────────────────────────────────────────────── */}
      <div className="sd-header">
        <div className="sd-header-left">
          <p className="sd-eyebrow">Lead With Tech · Analytics</p>
          <h1 className="sd-page-title">📊 Survey Dashboard</h1>
        </div>
        <button className="sd-logout-btn" onClick={() => {
          sessionStorage.removeItem('adminAuth');
          sessionStorage.removeItem('adminLoginTime');
          setAuthed(false);
        }}>
          🚪 Logout
        </button>
      </div>

      {/* ── Global KPIs ─────────────────────────────────────────────── */}
      <div className="sd-kpi-row">
        <KpiCard icon="📋" label="Total Surveys"    value={totalSurveys}   color="#de2c1e" />
        <KpiCard icon="✅" label="Active Surveys"   value={activeSurveys}  color="#10b981" />
        <KpiCard icon="👥" label="Total Responses"  value={totalResponses} color="#3b82f6" />
        <KpiCard icon="❓" label="Total Questions"  value={totalQuestions} color="#8b5cf6" />
      </div>

      {/* ── Survey Selector ─────────────────────────────────────────── */}
      <div className="sd-selector-bar">
        <label className="sd-selector-label">Filter by Survey</label>
        <select
          className="sd-selector"
          value={selectedId}
          onChange={e => handleSurveySelect(e.target.value)}
          disabled={surveysLoading}
        >
          <option value="">— Select a survey to view analytics —</option>
          {surveys.map(s => (
            <option key={s.id} value={s.id}>
              {s.title} ({s.status}) · {s.response_count ?? 0} responses
            </option>
          ))}
        </select>
        {surveysLoading && <span className="sd-inline-spinner" />}
      </div>

      {/* ── Per-survey detail ───────────────────────────────────────── */}
      {selectedId && (
        <div className="sd-survey-detail">

          {/* Survey header info */}
          {selectedSurveyMeta && (
            <div className="sd-survey-info-bar">
              <div className="sd-info-item">
                <span className="sd-info-icon">📋</span>
                <span>{selectedSurveyMeta.title}</span>
              </div>
              <div className="sd-info-item">
                <span className={`sd-status-badge sd-status-${selectedSurveyMeta.status}`}>
                  {selectedSurveyMeta.status}
                </span>
              </div>
              {selectedSurveyMeta.start_date && (
                <div className="sd-info-item">
                  <span className="sd-info-icon">📅</span>
                  <span>
                    {new Date(selectedSurveyMeta.start_date).toLocaleDateString('en-GB')}
                    {selectedSurveyMeta.end_date && ` → ${new Date(selectedSurveyMeta.end_date).toLocaleDateString('en-GB')}`}
                  </span>
                </div>
              )}
              <div className="sd-info-item">
                <span className="sd-info-icon">👥</span>
                <span>{selectedSurveyMeta.response_count ?? 0} total responses</span>
              </div>
              <div className="sd-info-item">
                <span className="sd-info-icon">🔒</span>
                <span>{selectedSurveyMeta.is_anonymous ? 'Anonymous' : 'Named respondents'}</span>
              </div>
            </div>
          )}

          {/* Loading state */}
          {detailLoading && !surveyDetail && (
            <div className="sd-loading-state">
              <div className="sd-spinner" />
              <p>Loading analytics…</p>
            </div>
          )}

          {/* Analytics */}
          {surveyDetail && surveyMeta && (
            <>
              {/* Per-survey KPIs */}
              <div className="sd-kpi-row sd-kpi-row-sm">
                <KpiCard
                  icon="👥"
                  label="Responses Loaded"
                  value={allResponses.length}
                  sub={surveyDetail.pagination?.total > allResponses.length ? `of ${surveyDetail.pagination.total} total` : 'all loaded'}
                  color="#3b82f6"
                />
                <KpiCard
                  icon="❓"
                  label="Questions"
                  value={surveyMeta.questions?.length ?? 0}
                  color="#8b5cf6"
                />
                <KpiCard
                  icon={surveyMeta.is_anonymous ? '🕵️' : '🧑'}
                  label="Anonymous"
                  value={surveyMeta.is_anonymous ? 'Yes' : 'No'}
                  color="#f59e0b"
                />
                {surveyDetail.pagination?.total > allResponses.length && (
                  <div className="sd-load-more-card">
                    <button
                      className="sd-btn-secondary"
                      onClick={loadMoreResponses}
                      disabled={detailLoading}
                    >
                      {detailLoading ? '⏳ Loading…' : `Load more responses (${surveyDetail.pagination.total - allResponses.length} remaining)`}
                    </button>
                  </div>
                )}
              </div>

              {/* Submissions timeline */}
              <SubmissionsTimeline responses={allResponses} />

              {/* Per-question charts */}
              <h3 className="sd-section-subtitle">📊 Question Breakdown</h3>
              <div className="sd-questions-grid">
                {(surveyMeta.questions || []).map(q => (
                  <QuestionBlock
                    key={q.id}
                    question={q}
                    analytics={surveyDetail.analytics || []}
                    responses={allResponses}
                  />
                ))}
              </div>

              {/* Raw responses table (non-anonymous only) */}
              {!surveyMeta.is_anonymous && allResponses.length > 0 && (
                <div className="sd-raw-responses">
                  <h3 className="sd-section-subtitle">📬 Individual Submissions ({allResponses.length})</h3>
                  <div className="sd-table-wrap">
                    <table className="sd-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Submitted</th>
                          <th>Answers</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allResponses.map((r, idx) => (
                          <tr key={r.id}>
                            <td>{idx + 1}</td>
                            <td>{r.respondent_name || '—'}</td>
                            <td>{r.respondent_email || '—'}</td>
                            <td>{new Date(r.submitted_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                            <td>
                              <details>
                                <summary className="sd-answers-toggle">View {r.answers.length} answer{r.answers.length !== 1 ? 's' : ''}</summary>
                                <ul className="sd-answers-list">
                                  {r.answers.map((a, ai) => (
                                    <li key={ai}>
                                      <strong>{a.question_text}:</strong>{' '}
                                      {a.option_text || a.answer_text || '—'}
                                    </li>
                                  ))}
                                </ul>
                              </details>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Empty state — no survey selected */}
      {!selectedId && !surveysLoading && (
        <div className="sd-empty-state">
          <div className="sd-empty-icon">📋</div>
          <h2>Select a Survey Above</h2>
          <p>Choose a survey from the dropdown to view detailed response analytics and charts.</p>
        </div>
      )}
    </main>
  );
}
